import { useEffect, useState } from "react";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import { IoIosSearch } from "react-icons/io";
import { FlaskConical } from "lucide-react";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";
import { ModalWrapper } from "../../../components/AdminSideComponents/common/ModalWrapper";
import api from "../../../configs/api";

const EMPTY_FORM = { gradeName: "" };

const GradeMixturesTab = () => {
  const [mixtures, setMixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingMixture, setEditingMixture] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/grade-mixtures`);
      setMixtures(res.data.data);
    } catch {
      showError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openAdd = () => {
    setEditingMixture(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (mixture) => {
    setEditingMixture(mixture);
    setForm({ gradeName: mixture.gradeName });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.gradeName.trim()) return showError("Grade name is required");
    try {
      setSaving(true);
      if (editingMixture) {
        await api.put(`/grade-mixtures/${editingMixture._id}`, form);
        showSuccess("Mixture updated");
      } else {
        await api.post(`/grade-mixtures`, form);
        showSuccess("Mixture created");
      }
      await fetchData();
      setModalOpen(false);
    } catch {
      showError("Failed to save mixture");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (mixture) => {
    const confirmed = await confirmAction({
      title: "Delete Mixture?",
      text: "This action cannot be undone.",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/grade-mixtures/${mixture._id}`);
      setMixtures((prev) => prev.filter((m) => m._id !== mixture._id));
      showSuccess("Mixture deleted");
    } catch {
      showError("Failed to delete mixture");
    }
  };

  const filtered = mixtures.filter((m) =>
    m.gradeName.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-descriptionText">
            <IoIosSearch className="size-4" />
          </span>
          <input
            type="text"
            placeholder="Search grade mixtures..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-[42px] w-full sm:w-[25rem] pl-9 pr-4 border border-borderLight outline-none focus:border-primaryAccent text-sm"
          />
        </div>
        <button
          onClick={openAdd}
          className="flex items-center gap-2 px-4 py-2 bg-primaryAccent text-white text-sm h-[42px]"
        >
          <FiPlus /> Add Grade Mixture
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="border border-borderLight bg-surfaceColor p-5 animate-pulse space-y-3"
            >
              <div className="h-3 bg-divider w-1/3" />
              <div className="h-3 bg-divider w-2/3" />
            </div>
          ))
        ) : filtered.length === 0 ? (
          <div className="col-span-full flex items-center justify-center py-24">
            <div className="max-w-sm border border-dashed border-borderLight p-10 text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primaryAccent/10 text-primaryAccent mb-4 mx-auto">
                <FlaskConical size={28} />
              </div>
              <p className="text-lg font-semibold text-headingText">
                No grade mixtures found
              </p>
            </div>
          </div>
        ) : (
          filtered.map((mixture) => (
            <div
              key={mixture._id}
              className="border border-borderLight bg-surfaceColor p-5 flex flex-col gap-3 hover:shadow-md transition"
            >
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold text-headingText">
                  {mixture.gradeName}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEdit(mixture)}
                    className="text-primaryAccent hover:opacity-70"
                  >
                    <FiEdit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(mixture)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      <ModalWrapper
        title={editingMixture ? "Edit Grade Mixture" : "Add Grade Mixture"}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onPrimaryAction={handleSave}
        primaryActionLabel={saving ? "Saving..." : "Save"}
        primaryLoading={saving}
        maxWidth="max-w-md"
      >
        <div className="space-y-4 text-sm">
          <div>
            <label className="block text-xs text-descriptionText mb-1">
              Grade Name *
            </label>
            <input
              type="text"
              value={form.gradeName}
              onChange={(e) => setForm({ gradeName: e.target.value })}
              className="w-full border border-borderLight px-3 py-2 outline-none focus:border-primaryAccent text-sm"
              placeholder="e.g. Grade A"
            />
          </div>
        </div>
      </ModalWrapper>
    </div>
  );
};

export default GradeMixturesTab;
